import { CafCorpusEntry, reconcileCafProjection, reconcileNavigationOperation } from '../../api/cafCorpus';

/**
 * Every AI Curation write goes through runSave, so staff only ever see "Saved"
 * or "Couldn't save". The server's guards (CAS versions, reviewed tokens,
 * idempotency keys, navigation holds) are untouched; this module runs the
 * follow-up checks the old recovery screens asked a person to run by hand:
 *
 * - 409 navigation_write_pending: reconcile the operation automatically.
 * - no reply / 5xx: replay an idempotent write, or read the record back.
 * - saved with linked records pending: finish them (finishPublication).
 *
 * "Nothing changed" is only said when the server refused or reconcile proved
 * not_committed. An unknown outcome is never reported as a failure that is
 * safe to repeat.
 */

export type SaveResult<T> = { ok: true; value: T | null } | { ok: false; message: string };
export type ReadBack<T> = { state: 'landed'; value: T } | { state: 'not_landed' } | { state: 'unknown' };

export interface SaveOptions<T> {
  /** Re-send the identical request. Only for idempotent writes. */
  replay?: () => PromiseLike<T>;
  /** Decide from the source of truth whether a write whose reply was lost landed. */
  readBack?: () => PromiseLike<ReadBack<T>>;
  /** Secondary work after a committed write (never turns success into failure). */
  finish?: (value: T) => PromiseLike<T>;
  sleep?: (ms: number) => Promise<void>;
}

export const AUTO_CHECK_REASON = 'Automatic check after a save with no reply';
export const AUTO_FINISH_REASON = 'Automatic: finish saving linked records';
export const MSG_NOTHING_CHANGED = "Couldn't save. Nothing changed. Try again.";
export const MSG_TRY_AGAIN = "Couldn't save. Try again.";
export const MSG_NOT_CONFIRMED = "Couldn't confirm the save. Wait a minute and refresh before trying again.";
/** An older backend without this write's route: the server refused, so nothing was written. */
export const MSG_NOT_AVAILABLE = "Couldn't save. This isn't available yet. Nothing changed.";

/** A compare-and-set write lost to someone else's change (spec §5: "Someone else changed this…"). */
export const MSG_CLASH = "Couldn't save. Someone else changed this while you had it open. Nothing was overwritten, and what you typed is kept.";
export const isClash = (message: string): boolean => message === MSG_CLASH;
const CLASH = 'Someone else changed this while you had it open. Nothing was overwritten, and what you typed is kept.';
const STEPS_WRONG = "Something in the steps isn't right. Check them and try again.";
const RELOAD = 'Please reload the page and try again.';

const CHECK_WAITS_MS = [0, 1000, 3000, 6000];
const FINISH_ATTEMPTS = 3;
const defaultSleep = (ms: number) => new Promise<void>((resolve) => { window.setTimeout(resolve, ms); });

type Failure = { type: 'refused'; message: string } | { type: 'pending'; operationId: string } | { type: 'unknown' };

interface HttpError {
  response?: { status?: number; data?: Record<string, unknown> };
}

const PAUSED = 'Saving is paused for a few minutes. Nothing changed. Try again shortly.';

export const REFUSAL_BY_CODE: Record<string, string> = {
  final_response_stance_required: 'Say whether this is a final response.',
  meaningful_correction_text_required: 'Say what the AI should have done.',
  correction_text_too_long: 'The note is too long.',
  review_revision_changed: 'Someone else gave feedback at the same time. Refresh and try again.',
  review_is_not_current: 'The feedback changed. Refresh and try again.',
  preview_expired: 'The preview has expired. Check the changes again.',
  task_changed_since_preview: 'The email changed since you checked. Check the changes again.',
  review_changed_since_preview: 'The feedback changed since you checked. Check the changes again.',
  assignment_claim_required: 'Choose the claim to attach it to.',
  archived_restore_must_be_explicit: 'This email is archived. Choose what should happen to it.',
  no_actual_change_requested: "That's already what happened to this email.",
  email_not_available: "This email has been archived or removed, so it can't be used as an example. Use Fix this email to bring it back first.",
  navigation_writes_fenced: PAUSED,
  navigation_control_unavailable: PAUSED,
  navigation_build_unsupported: PAUSED,
  // AI Curation > Automation (python-backend/app/routes/admin_caf_automation.py).
  switch_changed: CLASH,
  entry_changed: CLASH,
  reviewed_guidance_changed: CLASH,
  workflow_changed: CLASH,
  control_changed: CLASH,
  link_changed: CLASH,
  seeds_changed: CLASH,
  effect_capacity_changed: CLASH,
  workflow_revision_changed: CLASH,
  publication_revision_already_used: CLASH,
  runs_not_available: 'This is available after the next server update.',
  client_emails_not_checked: 'Check its client emails first. Open it and preview each one.',
  template_review_required: 'Check every client email first.',
  template_source_required: "One of its client emails isn't ready yet.",
  template_changed: 'This email changed since you checked it. Preview it again.',
  email_not_set_up: "One of its client emails isn't set up yet, so it can't be used.",
  archive_example_has_no_claim: "An archived email has no claim, so it can't have a workflow.",
  final_response_workflow_required: 'An example marked Final response needs a workflow that ends with Put on list: Final response, with no Mark done, Archive or emails. Keep only Attach, Add a note and Set deadline (FOS deadline).',
  deadline_twice: 'A workflow can set one deadline. Remove the extra Set deadline step.',
  final_response_deadline: 'The Final response list only takes the FOS deadline. Choose FOS deadline (6 months), or put the email on another list.',
  not_a_draft: 'Only a draft that has never watched can be deleted.',
  not_saved_for_watching: 'Save it first.',
  invalid_transition: "That change isn't possible from here. Refresh the page.",
  cannot_duplicate: "This workflow can't be copied here.",
  unknown_workflow: 'That workflow no longer exists. Refresh the page.',
  workflow_not_found: 'That workflow no longer exists. Refresh the page.',
  example_not_found: 'That example no longer exists. Refresh the page.',
  examples_unavailable: "The approved examples couldn't be read. Nothing changed. Try again.",
  authority_unavailable: "Couldn't check the letters of authority. Nothing changed. Try again.",
  automation_not_ready: 'Automation needs the latest database update. Nothing changed.',
  // automation_unavailable is deliberately absent: the backend raises it for any error during a write, possibly after
  // a partial write, so it is an unknown outcome (couldn't confirm), never "Nothing changed".
  json_required: RELOAD,
  malformed_json: RELOAD,
  unknown_fields: RELOAD,
  invalid_name: 'Give it a name.',
  invalid_as_draft: "A draft can't be saved this way. Refresh the page and try again.",
  invalid_counts: "The example counts didn't add up. Refresh the page and try again.",
  invalid_revision: 'This page is out of date. Refresh it and try again.',
  invalid_window: 'That period is not available.',
  invalid_reviewed_emails: 'Check every client email first.',
  draft_not_allowed: "A workflow that has already run can't go back to a draft.",
  no_steps: STEPS_WRONG,
  first_not_attach: STEPS_WRONG,
  attach_twice: STEPS_WRONG,
  early_ending: STEPS_WRONG,
  no_ending: STEPS_WRONG,
  wait_without_email: STEPS_WRONG,
  too_many_steps: STEPS_WRONG,
  invalid_step: STEPS_WRONG,
};

const codeOf = (data: Record<string, unknown>) =>
  typeof data.code === 'string' ? data.code : typeof data.error === 'string' ? data.error : '';

/**
 * The app's global 404 handler (python-backend/app/__init__.py) answers every
 * unknown route with exactly {"error": "Not Found"}; a route's own 404 always
 * names what's missing. So this is how the frontend tells "this backend is
 * older and has no such endpoint yet" from "the record is gone".
 */
export const isMissingRoute = (err: unknown): boolean => {
  const response = (err as HttpError | null)?.response;
  return response?.status === 404 && response.data?.error === 'Not Found';
};

/** Only for a response the engine already knows is a refusal (classifyFailure decides). */
function plainRefusal(err: unknown): string {
  const response = (err as HttpError)?.response;
  const status = response?.status;
  const data = response?.data || {};
  const code = codeOf(data);
  if (code && REFUSAL_BY_CODE[code]) return `Couldn't save. ${REFUSAL_BY_CODE[code]}`;
  if (status === 401) return "Couldn't save. Your session has ended. Sign in again.";
  if (status === 403) return "Couldn't save. You don't have permission to do this.";
  if (isMissingRoute(err)) return MSG_NOT_AVAILABLE;
  if (status === 404) return "Couldn't save. It no longer exists. Refresh the page.";
  if (status === 409 && data.held === true) return "Couldn't save. An admin needs to check this example first.";
  if (status === 409 && data.retryable === true) {
    return "Couldn't save. Another change to this sender is still being saved. Try again in a minute.";
  }
  if (status === 409) return "Couldn't save. Someone else changed this at the same time. Refresh and try again.";
  if (status === 410) return "Couldn't save. The preview has expired. Check the changes again.";
  if (status === 413) return "Couldn't save. That's too much text.";
  if (status === 429) return "Couldn't save. Too many requests. Try again in a minute.";
  if (status === 503) return MSG_NOTHING_CHANGED;
  return "Couldn't save. Something in the form isn't right. Check it and try again.";
}

export function classifyFailure(err: unknown): Failure {
  const response = (err as HttpError)?.response;
  if (!response || typeof response.status !== 'number') return { type: 'unknown' };
  const { status } = response;
  const data = response.data || {};
  if (status === 409 && data.code === 'navigation_write_pending' && typeof data.operation_id === 'string') {
    return { type: 'pending', operationId: data.operation_id };
  }
  if (status === 503 && REFUSAL_BY_CODE[codeOf(data)]) return { type: 'refused', message: plainRefusal(err) };
  // A request timeout is a lost reply, not a refusal: the write may have landed.
  if (status >= 500 || status === 408) return { type: 'unknown' };
  return { type: 'refused', message: plainRefusal(err) };
}

// A replay carries the identical payload as the original attempt, so only
// these client-error statuses -- ones the original would have hit just the
// same -- may stay refusals when the original attempt's own outcome is
// unknown. Every other replay refusal (409 conflicts, 503 pauses, etc.)
// proves nothing about whether the original landed.
const HARD_REFUSAL_STATUSES = [400, 401, 403, 413];

const isHardRefusalStatus = (err: unknown): boolean => {
  const status = (err as HttpError)?.response?.status;
  return typeof status === 'number' && HARD_REFUSAL_STATUSES.includes(status);
};

export async function confirmOperation(
  operationId: string,
  sleep: (ms: number) => Promise<void> = defaultSleep,
): Promise<'committed' | 'not_committed' | 'pending'> {
  for (const wait of CHECK_WAITS_MS) {
    if (wait) await sleep(wait);
    try {
      const outcome = await reconcileNavigationOperation(operationId, AUTO_CHECK_REASON);
      if (outcome.status === 'committed') return 'committed';
      if (outcome.status === 'not_committed') return 'not_committed';
    } catch (err) {
      if (classifyFailure(err).type === 'refused') return 'pending';
    }
  }
  return 'pending';
}

export async function runSave<T>(attempt: () => PromiseLike<T>, options: SaveOptions<T> = {}): Promise<SaveResult<T>> {
  const sleep = options.sleep ?? defaultSleep;
  const succeed = async (value: T | null): Promise<SaveResult<T>> => {
    if (value !== null && options.finish) {
      try {
        return { ok: true, value: await options.finish(value) };
      } catch {
        return { ok: true, value };
      }
    }
    return { ok: true, value };
  };
  const readBack = async (): Promise<ReadBack<T>> => {
    if (!options.readBack) return { state: 'unknown' };
    try {
      return await options.readBack();
    } catch {
      return { state: 'unknown' };
    }
  };
  // The FIRST attempt raised this pending navigation operation directly:
  // its own "not_committed" proves nothing changed.
  const resolveFirstAttemptPending = async (operationId: string): Promise<SaveResult<T>> => {
    const outcome = await confirmOperation(operationId, sleep);
    if (outcome === 'not_committed') return { ok: false, message: MSG_NOTHING_CHANGED };
    if (outcome === 'pending') return { ok: false, message: MSG_NOT_CONFIRMED };
    const back = await readBack();
    return succeed(back.state === 'landed' ? back.value : null);
  };
  // The REPLAY raised this pending navigation operation. Its "not_committed"
  // only proves the replay's own write didn't land (e.g. the replay lost an
  // etag/version race) -- the original attempt's outcome, already unknown,
  // is untouched by that. So anything short of a confirmed commit here falls
  // through to read-back exactly like any other still-unknown result.
  const resolveReplayPending = async (operationId: string): Promise<SaveResult<T>> => {
    const outcome = await confirmOperation(operationId, sleep);
    if (outcome === 'committed') {
      const back = await readBack();
      return succeed(back.state === 'landed' ? back.value : null);
    }
    if (outcome === 'pending') return { ok: false, message: MSG_NOT_CONFIRMED };
    const back = await readBack();
    if (back.state === 'landed') return succeed(back.value);
    if (back.state === 'not_landed') return { ok: false, message: MSG_TRY_AGAIN };
    return { ok: false, message: MSG_NOT_CONFIRMED };
  };
  try {
    return await succeed(await attempt());
  } catch (err) {
    const failure = classifyFailure(err);
    if (failure.type === 'refused') return { ok: false, message: failure.message };
    if (failure.type === 'pending') return resolveFirstAttemptPending(failure.operationId);
    if (options.replay) {
      try {
        return await succeed(await options.replay());
      } catch (again) {
        const second = classifyFailure(again);
        if (second.type === 'pending') return resolveReplayPending(second.operationId);
        // The first attempt's outcome is still unknown. A refused or conflicting
        // replay only proves the replay itself didn't land -- it says nothing
        // about the original -- so it is treated as unknown too and falls
        // through to read-back below, UNLESS the replay sent the identical
        // payload and got a hard client-error refusal (400/401/403/413) that
        // the original would have hit just the same.
        if (second.type === 'refused' && isHardRefusalStatus(again)) {
          return { ok: false, message: second.message };
        }
      }
    }
    const back = await readBack();
    if (back.state === 'landed') return succeed(back.value);
    if (back.state === 'not_landed') return { ok: false, message: MSG_TRY_AGAIN };
    return { ok: false, message: MSG_NOT_CONFIRMED };
  }
}

export const projectionSuperseded = (entry: CafCorpusEntry) => entry.projection_status === 'superseded';

export const projectionPending = (entry: CafCorpusEntry) =>
  !projectionSuperseded(entry) && entry.publication_status !== 'complete'
  && (entry.publication_status === 'partial' || Boolean(entry.projection_pending));

/** The example is committed; finish its linked records (group link, override stamp) automatically. */
export async function finishPublication(
  entry: CafCorpusEntry,
  sleep: (ms: number) => Promise<void> = defaultSleep,
): Promise<CafCorpusEntry> {
  let current = entry;
  for (let attempt = 0; attempt < FINISH_ATTEMPTS && projectionPending(current) && current.version; attempt += 1) {
    if (attempt) await sleep(1000 * attempt);
    try {
      current = await reconcileCafProjection(current.id, {
        sender: current.sender, expected_version: current.version, reason: AUTO_FINISH_REASON,
      });
    } catch {
      // The example itself is saved; the next open of its group finishes the link.
    }
  }
  return current;
}

export const newIdempotencyKey = (prefix: string): string => {
  const random = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${random}`;
};
